import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getOpsAccount, linkOpsAccount, unlinkOpsAccount, linkOpsNotifyAccount, unlinkOpsNotifyAccount, opsErr } from '../../api/ops';
import { useToast } from '../../components/Toast';
import ImageLightbox from '../../components/ImageLightbox';
import { scanBarcode } from './scanner';
import './ops.css';

// ================= Tài khoản PMBox (OpsGate) =================
// Mọi màn VẬN HÀNH bọc trong <OpsGate>: chưa liên kết / phiên PMBox hết hạn -> hiện form đăng nhập PMBox.
const OpsCtx = createContext(null);
export const useOps = () => useContext(OpsCtx);

function OpsLogin({ account, onDone }) {
  const toast = useToast();
  const [username, setUsername] = useState(account?.username || '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const a = await linkOpsAccount(username.trim(), password, remember);
      toast(`Đã liên kết PMBox: ${a.name || a.username}`);
      onDone(a);
    } catch (err) {
      toast(opsErr(err, 'Đăng nhập PMBox thất bại'), 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card ops-login" onSubmit={submit}>
      <h3><i className="ti ti-truck-delivery" /> Đăng nhập tài khoản PMBox</h3>
      <div className="small muted" style={{ marginBottom: 12, lineHeight: 1.6 }}>
        Chức năng Vận hành dùng <b>tài khoản PMBox của chính bạn</b> (như app "i love GTS") để PMBox ghi đúng người nhặt/giao hàng.
        {account?.linked === true && ' Phiên đăng nhập đã hết hạn — đăng nhập lại.'}
      </div>
      <div className="field">
        <label className="small">Tài khoản</label>
        <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />
      </div>
      <div className="field">
        <label className="small">Mật khẩu</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
      </div>
      <label className="small row" style={{ gap: 6, marginBottom: 12 }}>
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
        Ghi nhớ (mã hoá) để tự đăng nhập lại khi hết phiên
      </label>
      <button className="btn" style={{ width: '100%' }} disabled={busy}>{busy ? 'Đang đăng nhập…' : 'Đăng nhập'}</button>
    </form>
  );
}

// Form THÊM 1 tài khoản CHỈ-NHẬN-THÔNG-BÁO (khác OpsLogin: không đổi tài khoản chính đang thao tác).
function NotifyAddForm({ onDone, onCancel }) {
  const toast = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const a = await linkOpsNotifyAccount(username.trim(), password, remember);
      toast(`Đã thêm tài khoản nhận thông báo: ${a.name || a.username}`);
      onDone();
    } catch (err) {
      toast(opsErr(err, 'Thêm tài khoản thất bại'), 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="ops-add-form" onSubmit={submit}>
      <div className="field">
        <label className="small">Tài khoản PMBox</label>
        <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" required />
      </div>
      <div className="field">
        <label className="small">Mật khẩu</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" required />
      </div>
      <label className="small row" style={{ gap: 6 }}>
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
        Ghi nhớ (mã hoá) để tự đăng nhập lại
      </label>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn sm" disabled={busy}>{busy ? 'Đang thêm…' : 'Thêm tài khoản'}</button>
        <button type="button" className="btn sm ghost" onClick={onCancel} disabled={busy}>Huỷ</button>
      </div>
    </form>
  );
}

// Khối quản lý các tài khoản CHỈ-NHẬN-THÔNG-BÁO (thêm/gỡ). Tách riêng để dùng được CẢ khi đã có tài khoản chính
// (trong thanh OpsAccountBar) LẪN khi chưa/đã out tài khoản chính (để tài khoản phụ không bị "bỏ rơi").
function OpsNotifyManager({ account, onReload }) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const notify = account.notifyAccounts || [];
  const max = account.maxNotifyAccounts || 0;
  const remove = async (id) => {
    try { await unlinkOpsNotifyAccount(id); await onReload(); } catch (e) { toast(opsErr(e, 'Không gỡ được'), 'danger'); }
  };
  return (
    <div className="ops-acc-notify">
      <div className="small muted">Tài khoản nhận thông báo thêm</div>
      {notify.map((n) => (
        <div key={n.id} className="ops-notify-row">
          <i className="ti ti-bell" /> <b>{n.name || n.username}</b>
          {n.staffCode ? <span className="muted">&nbsp;({n.staffCode})</span> : null}
          <span className="ops-tag">Chỉ nhận thông báo</span>
          <button type="button" className="btn xs ghost ops-notify-x" onClick={() => remove(n.id)} aria-label="Gỡ tài khoản">
            <i className="ti ti-x" />
          </button>
        </div>
      ))}
      {adding ? (
        <NotifyAddForm
          onDone={async () => { setAdding(false); try { await onReload(); } catch (e) { toast(opsErr(e, 'Không tải lại được danh sách')); } }}
          onCancel={() => setAdding(false)}
        />
      ) : notify.length < max ? (
        <button type="button" className="btn xs ops-add-btn" onClick={() => setAdding(true)}>
          <i className="ti ti-plus" /> Thêm tài khoản nhận thông báo (còn {max - notify.length})
        </button>
      ) : (
        <div className="small muted">Đã đạt tối đa {max} tài khoản nhận thông báo.</div>
      )}
    </div>
  );
}

// Thanh tài khoản đầu mỗi màn Vận hành: tài khoản CHÍNH (thao tác) + khối quản lý tài khoản chỉ-nhận-thông-báo.
function OpsAccountBar({ account, onReload, onUnlinkPrimary }) {
  return (
    <div className="ops-acc-box">
      <div className="ops-acc-primary">
        <i className="ti ti-user-check" /> PMBox: <b>{account.name || account.username}</b>
        {account.staffCode ? ` (${account.staffCode})` : ''}
        <span className="ops-tag ops-tag-live">Đang thao tác</span>
        <button type="button" className="btn xs ghost" onClick={onUnlinkPrimary}>Đăng xuất tài khoản chính</button>
      </div>
      <OpsNotifyManager account={account} onReload={onReload} />
    </div>
  );
}

// onBack (tuỳ chọn): có khi màn này được mở từ màn "Vận hành" ở thanh dưới -> hiện nút quay lại đó.
export function OpsGate({ title, sub, actions, children, onBack }) {
  const toast = useToast();
  const [account, setAccount] = useState(null); // null = đang tải
  const [needLogin, setNeedLogin] = useState(false);

  const reload = useCallback(
    () => getOpsAccount().then((a) => { setAccount(a); setNeedLogin(!a.linked); return a; }),
    []
  );

  useEffect(() => {
    reload().catch((e) => { setAccount({ linked: false }); setNeedLogin(true); toast(opsErr(e), 'danger'); });
    const onExpired = () => setNeedLogin(true);
    window.addEventListener('ft:ops-login', onExpired);
    return () => window.removeEventListener('ft:ops-login', onExpired);
  }, [toast, reload]);

  const unlink = async () => {
    await unlinkOpsAccount().catch(() => {});
    // Tải lại để GIỮ danh sách tài khoản chỉ-nhận-thông-báo (out chính KHÔNG xoá phụ) -> vẫn quản lý được.
    await reload().catch(() => { setAccount({ linked: false, base: account?.base, notifyAccounts: account?.notifyAccounts || [], maxNotifyAccounts: account?.maxNotifyAccounts }); setNeedLogin(true); });
  };

  return (
    <div className="page ops-page">
      <div className="page-head">
        <div className="ops-head-main">
          {onBack && (
            <button type="button" className="btn sm ghost ops-back" onClick={onBack} title="Quay lại Vận hành" aria-label="Quay lại">
              <i className="ti ti-arrow-left" />
            </button>
          )}
          <div className="ops-head-txt">
            <h2>{title}</h2>
            {sub && <div className="sub">{sub}</div>}
          </div>
        </div>
        {actions && !needLogin && <div className="row">{actions}</div>}
      </div>
      {account && account.linked && !needLogin && (
        <OpsAccountBar account={account} onReload={reload} onUnlinkPrimary={unlink} />
      )}
      {account === null ? (
        <div className="empty">Đang tải…</div>
      ) : needLogin ? (
        <>
          {/* Set NGAY từ response (kèm notifyAccounts/maxNotifyAccounts) để form đóng chắc chắn; reload nền phòng lệch. */}
          <OpsLogin account={account} onDone={(a) => { setAccount(a); setNeedLogin(!a.linked); reload().catch(() => {}); }} />
          {/* Chưa có tài khoản chính nhưng ĐÃ có tài khoản chỉ-nhận-thông-báo -> vẫn cho quản lý (không bỏ rơi). */}
          {(account.notifyAccounts || []).length > 0 && (
            <div className="ops-acc-box" style={{ marginTop: 14 }}>
              <OpsNotifyManager account={account} onReload={reload} />
            </div>
          )}
        </>
      ) : (
        <OpsCtx.Provider value={account}>{children}</OpsCtx.Provider>
      )}
    </div>
  );
}

// ================= Ô tìm/quét mã =================
// Enter hoặc nút Tìm -> onSubmit(mã). Nút camera -> quét rồi tự tìm. Máy quét cầm tay (kiểu bàn phím) gõ + Enter.
export function ScanSearch({ value, onChange, onSubmit, placeholder = 'Nhập hoặc quét mã…', busy, autoFocus, liveSearch }) {
  const toast = useToast();
  const scan = async () => {
    try {
      const code = await scanBarcode();
      if (code) { onChange(code); onSubmit(code, { scanned: true }); }
    } catch (e) {
      toast(e.message || 'Không quét được', 'danger');
    }
  };
  return (
    <form className="ops-search" onSubmit={(e) => { e.preventDefault(); onSubmit(value.trim()); }}>
      <i className="ti ti-search" />
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); if (liveSearch) onSubmit(e.target.value.trim(), { live: true }); }}
        // Bắt Enter trực tiếp: vài máy quét cầm tay/WebView không kích submit ngầm của form.
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); onSubmit(e.currentTarget.value.trim()); } }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        enterKeyHint="search"
      />
      {!liveSearch && <button type="submit" className="btn sm" disabled={busy}>Tìm</button>}
      <button type="button" className="btn sm ghost ops-scan-btn" onClick={scan} title="Quét mã vạch / QR">
        <i className="ti ti-scan" />
      </button>
    </form>
  );
}

// ================= Ngày giờ / tiền =================
const pad = (n) => String(n).padStart(2, '0');
export const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
export const toInputDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const inputToDmy = (s) => { const [y, m, d] = String(s).split('-'); return `${d}/${m}/${y}`; };
export const startOfInput = (s) => new Date(`${s}T00:00:00`);
export const endOfInput = (s) => new Date(`${s}T23:59:59`);
export function fmtDateTime(v, addHours = 0) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  if (addHours) d.setHours(d.getHours() + addHours);
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// Giống DateTime.toIso8601String() của Dart (giờ máy, không 'Z') — app GTS gửi from/to kiểu này.
export const localIso = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.000`;
export const fmtMoney = (v) => (v == null || v === '' ? '' : Number(v).toLocaleString('vi-VN'));

export function DateRange({ from, to, onChange }) {
  return (
    <div className="ops-daterange">
      <input type="date" value={from} max={to} onChange={(e) => onChange(e.target.value, to)} />
      <span className="muted">→</span>
      <input type="date" value={to} min={from} onChange={(e) => onChange(from, e.target.value)} />
    </div>
  );
}

// ================= Ảnh =================
// Ảnh SocSon lưu đường dẫn tương đối -> ghép với host SocSon.
export const imgUrl = (base, src) => (!src ? '' : /^https?:\/\//i.test(src) ? src : `${base || 'https://socsonship.com'}${src.startsWith('/') ? '' : '/'}${src}`);

export function ImgGrid({ urls }) {
  const [view, setView] = useState('');
  if (!urls?.length) return null;
  return (
    <>
      <div className="ops-imggrid">
        {urls.map((u, i) => (
          <button key={u + i} type="button" onClick={() => setView(u)}>
            <img src={u} alt="" loading="lazy" />
          </button>
        ))}
      </div>
      {view && <ImageLightbox src={view} filename={view.split('/').pop()} onClose={() => setView('')} />}
    </>
  );
}

// Nén ảnh về JPEG (cạnh dài tối đa 1920px, chất lượng 0.8) trước khi gửi SocSon.
export function compressImage(file, maxSide = 1920, quality = 0.8) {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, maxSide / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * k);
      c.height = Math.round(img.height * k);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => {
        URL.revokeObjectURL(url);
        if (!b) return resolve(file);
        resolve(new File([b], `${(file.name || 'image').replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' }));
      }, 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// Chọn ảnh: chụp camera hoặc chọn nhiều từ thư viện. files = [{ file, url }].
export function PhotoPicker({ files, setFiles }) {
  const camRef = useRef(null);
  const libRef = useRef(null);
  const add = useCallback(async (list) => {
    const arr = await Promise.all(Array.from(list || []).map((f) => compressImage(f)));
    setFiles((prev) => [...prev, ...arr.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  }, [setFiles]);
  const remove = (i) => setFiles((prev) => { URL.revokeObjectURL(prev[i].url); return prev.filter((_, j) => j !== i); });
  return (
    <div>
      <div className="row">
        <button type="button" className="btn sm ghost" onClick={() => camRef.current.click()}><i className="ti ti-camera" /> Chụp ảnh</button>
        <button type="button" className="btn sm ghost" onClick={() => libRef.current.click()}><i className="ti ti-photo" /> Chọn ảnh</button>
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
        <input ref={libRef} type="file" accept="image/*" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
      </div>
      {files.length > 0 && (
        <div className="ops-imggrid" style={{ marginTop: 10 }}>
          {files.map((f, i) => (
            <div key={f.url} className="ops-thumb">
              <img src={f.url} alt="" />
              <button type="button" onClick={() => remove(i)} title="Bỏ ảnh">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================= Chi tiết đơn (quote) =================
export const IMAGE_TYPES = [
  { key: 'quote_to_foreign', label: 'Ảnh từ kho nước ngoài' },
  { key: 'quote_to_vietnam', label: 'Ảnh từ kho Việt Nam' },
  { key: 'quote_by_sale', label: 'Ảnh chụp bởi sale' },
];

// Nhóm ảnh image_capture theo loại -> theo (ngày giờ tải + người tải).
export function QuoteImages({ quote, base }) {
  const caps = quote?.image_capture || [];
  if (!caps.length) return <div className="small muted">Chưa có hình ảnh.</div>;
  return IMAGE_TYPES.map((t) => {
    const list = caps.filter((c) => c.type_image === t.key);
    if (!list.length) return null;
    const groups = {};
    list.forEach((c) => {
      const k = `${fmtDateTime(c.upload_date)} · ${c.upload_by?.name || c.upload_by?.staff_code || ''}`;
      (groups[k] = groups[k] || []).push(imgUrl(base, c.source));
    });
    return (
      <div key={t.key} className="ops-imgsec">
        <div className="ops-imgsec-title">{t.label}</div>
        {Object.entries(groups).map(([k, urls]) => (
          <div key={k} style={{ marginBottom: 8 }}>
            <div className="small muted" style={{ marginBottom: 4 }}>{k}</div>
            <ImgGrid urls={urls} />
          </div>
        ))}
      </div>
    );
  });
}

export function QuoteDetail({ quote, base }) {
  if (!quote) return null;
  const rows = [
    ['Mã đơn hàng', quote.code],
    ['Mã tracking', quote.tracking_no],
    ['Sản phẩm', quote.link ? <a href={quote.link} target="_blank" rel="noreferrer">{quote.name || quote.link}</a> : quote.name],
    ['Khách hàng', quote.user ? `${quote.user.customer_no || ''} - ${quote.user.full_name || ''}` : quote.customer],
    ['Cân nặng', quote.weight != null ? `${quote.weight} kg` : ''],
    ['Số lượng', quote.quality],
    ['Tuyến mua', quote.route_code],
    ['Giá SP', quote.totalCurrency ?? quote.total],
    ['Mã MAWB - Thùng', quote.mawb_no],
    ['Vị trí', quote.position],
    ['Trạng thái', quote.status_label],
    ['Ghi chú', quote.note],
  ].filter(([, v]) => v !== undefined && v !== null && v !== '');
  return (
    <div className="card">
      <div className="ops-kv">
        {rows.map(([k, v]) => (
          <div key={k}><span>{k}</span><b>{v}</b></div>
        ))}
      </div>
      <h3 style={{ marginTop: 16 }}><i className="ti ti-photo" /> Hình ảnh đơn hàng</h3>
      <QuoteImages quote={quote} base={base} />
    </div>
  );
}
